import React, { useState, useEffect } from "react";
import { ShieldAlert, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

export const CookieConsent: React.FC = () => {
  const { t } = useTranslation("CookieConsent");
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem("skjeta_cookie_consent");
    if (!consent) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem(
      "skjeta_cookie_consent",
      JSON.stringify({
        necessary: true,
        analytics: true,
        marketing: true,
        timestamp: new Date().toISOString(),
      }),
    );
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    localStorage.setItem(
      "skjeta_cookie_consent",
      JSON.stringify({
        necessary: true,
        analytics: false,
        marketing: false,
        timestamp: new Date().toISOString(),
      }),
    );
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem(
      "skjeta_cookie_consent",
      JSON.stringify({
        ...preferences,
        timestamp: new Date().toISOString(),
      }),
    );
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-zinc-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in slide-in-from-bottom-8 sm:zoom-in-95 duration-500">
        {!showPreferences ? (
          <div className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4 text-skjeta-green">
              <ShieldAlert size={28} />
              <h2 className="text-xl font-bold text-white">
                {t("title")}
              </h2>
            </div>

            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              {t("description")}
            </p>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleAcceptAll}
                className="w-full bg-skjeta-green hover:bg-skjeta-green/90 text-black font-bold py-3 rounded-xl transition-colors"
              >
                {t("acceptAll")}
              </button>
              <div className="flex gap-3">
                <button
                  onClick={handleRejectAll}
                  className="flex-1 bg-white/5 hover:bg-white/10 text-white font-medium py-3 rounded-xl transition-colors border border-white/5"
                >
                  {t("rejectAll")}
                </button>
                <button
                  onClick={() => setShowPreferences(true)}
                  className="flex-1 bg-transparent hover:bg-white/5 text-gray-400 hover:text-white font-medium py-3 rounded-xl transition-colors"
                >
                  {t("customize")}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => setShowPreferences(false)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors -ml-2"
              >
                <ChevronRight className="rotate-180" size={20} />
              </button>
              <h2 className="text-xl font-bold text-white">
                {t("customizeTitle")}
              </h2>
            </div>

            <div className="space-y-4 mb-8">
              {/* Necessary */}
              <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-white/5 border border-white/10">
                <div>
                  <h3 className="font-bold text-white text-sm mb-1">
                    {t("necessaryTitle")}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {t("necessaryDescription")}
                  </p>
                </div>
                <div className="shrink-0 pt-1">
                  <div className="w-10 h-6 bg-skjeta-green/50 rounded-full relative opacity-50 cursor-not-allowed">
                    <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Analytics */}
              <div
                className="flex items-start justify-between gap-4 p-4 rounded-xl bg-white/5 border border-white/5 cursor-pointer hover:bg-white/10 transition-colors"
                onClick={() =>
                  setPreferences((p) => ({ ...p, analytics: !p.analytics }))
                }
              >
                <div>
                  <h3 className="font-bold text-white text-sm mb-1">
                    {t("analyticsTitle")}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {t("analyticsDescription")}
                  </p>
                </div>
                <div className="shrink-0 pt-1">
                  <div
                    className={`w-10 h-6 rounded-full relative transition-colors ${preferences.analytics ? "bg-skjeta-green" : "bg-gray-700"}`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${preferences.analytics ? "right-1" : "left-1"}`}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Marketing */}
              <div
                className="flex items-start justify-between gap-4 p-4 rounded-xl bg-white/5 border border-white/5 cursor-pointer hover:bg-white/10 transition-colors"
                onClick={() =>
                  setPreferences((p) => ({ ...p, marketing: !p.marketing }))
                }
              >
                <div>
                  <h3 className="font-bold text-white text-sm mb-1">
                    {t("marketingTitle")}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {t("marketingDescription")}
                  </p>
                </div>
                <div className="shrink-0 pt-1">
                  <div
                    className={`w-10 h-6 rounded-full relative transition-colors ${preferences.marketing ? "bg-skjeta-green" : "bg-gray-700"}`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${preferences.marketing ? "right-1" : "left-1"}`}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSavePreferences}
                className="flex-1 bg-skjeta-green hover:bg-skjeta-green/90 text-black font-bold py-3 rounded-xl transition-colors"
              >
                {t("savePreferences")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
